<template>
  <div
    v-if="isIstLayoutCardEnabled"
    class="ist-card"
    v-on:click="cardClicked"
    :class="{ clickable: clickable() }"
  >
    <div class="ist-card__body" :style="minh">
      <div class="ist-card__toolbar" v-if="hasToolbarSlot">
        <dtpToolbar class="text-primary ist-actionbar">
          <template #default>
            <slot name="toolbar" />
          </template>
          <template #center>
            <slot name="toolbarcenter" />
          </template>
          <template #end>
            <slot name="toolbarend" />
          </template>
          <template #start>
            <slot name="toolbarstart" />
          </template>
        </dtpToolbar>
      </div>
      <dtpIcon icon-class="iconstyle" :name="iconname" v-if="iconname" />
      <div class="ist-card__body__section">
        <slot />
      </div>
      <div
        class="ist-card__splitter ist-card__move-to-bottom"
        v-if="pTags.length > 0"
      />
      <div class="ist-card__body__section" v-if="pTags.length > 0">
        <div class="ist-arrow-right_start">
          <div
            class="ist-arrow-right"
            v-for="tag in pTags"
            v-bind:key="tag.name"
            v-bind:style="[setArrowColor(tag.color)]"
            v-on:click="tagClicked(tag)"
            :class="{ clickable: clickable() }"
          >
            {{ tag.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="border border-gray-300 rounded-lg p-4"
    @click="cardClicked($event)"
  >
    <div v-if="hasToolbarSlot" style="margin-bottom: 16px">
      <dtpToolbar>
        <template #default v-if="$slots.toolbar">
          <slot name="toolbar" />
        </template>
        <template #center v-if="$slots.toolbarcenter">
          <slot name="toolbarcenter" />
        </template>
        <template #end v-if="$slots.toolbarend">
          <slot name="toolbarend" />
        </template>
        <template #start v-if="$slots.toolbarstart">
          <slot name="toolbarstart" />
        </template>
      </dtpToolbar>
    </div>
    <dtpIcon icon-class="iconstyle" :name="iconname" v-if="iconname" />
    <slot />
    <div
      v-if="pTags.length > 0"
      style="border-top: 1px solid #ddd; margin-top: auto; padding: 16px"
    >
      <div style="display: flex; flex-wrap: wrap; gap: 8px">
        <CuiTag
          v-for="tag in pTags"
          v-bind:key="tag.name"
          :value="tag.name"
          severity="other"
          :style="{
            cursor: clickable() ? 'pointer' : 'default',
            '--cui-tag-bg': tag.color || props.arrowcolor,
            '--cui-tag-text': '#000',
          }"
          @click="tagClicked(tag)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  getCurrentInstance,
  onMounted,
  onUpdated,
  ref,
  useAttrs,
  watch,
} from "vue";
import { CardTags } from "./IstLayoutCard";
import { CuiTag } from "@ist/commonui-components";
import { useFeatureFlags } from "../composables/useFeatureFlags";

defineOptions({ name: "ISTLayoutCard" });

const { isEnabled: checkEnabled } = useFeatureFlags();
const isIstLayoutCardEnabled = checkEnabled("IstLayoutCard");

const props = withDefaults(
  defineProps<{
    iconname?: string;
    minh?: string;
    arrowcolor?: string;
    tags?: CardTags[] | null;
    isClickable?: boolean | null;
  }>(),
  {
    minh: "min-height: 180px",
    arrowcolor: "#f00000",
    tags: null,
    isClickable: null,
  },
);

const emit = defineEmits<{
  (e: "click", evt: Event, ctx: any): void;
  (e: "tagclick", tag: any, ctx: any): void;
}>();

const pTags = ref<CardTags[]>([]);

onMounted(() => {
  pTags.value.length = 0;
  if (props.tags) {
    pTags.value = props.tags;
  }
});

watch(
  () => props.tags,
  (newtags) => {
    pTags.value = [];
    if (newtags) {
      pTags.value = newtags as CardTags[];
    }
  },
);

const cardClicked = (event: Event) => {
  emit("click", event, getCurrentInstance()?.proxy);
};

const tagClicked = (event: any) => {
  emit("tagclick", event, getCurrentInstance()?.proxy);
};

const setArrowColor = (arraycolor?: string) => {
  return {
    "background-color": arraycolor ?? props.arrowcolor,
    "border-color": arraycolor ?? props.arrowcolor,
  } as Record<string, string>;
};

const attrs = useAttrs();
const hasClickListener = ref(false);
const recomputeClickable = () => {
  // explicit prop override
  if (props.isClickable !== null && props.isClickable !== undefined) {
    hasClickListener.value = !!props.isClickable;
    return;
  }
  const instance = getCurrentInstance();
  const sources: Array<Record<string, unknown>> = [];
  if (instance?.vnode?.props && typeof instance.vnode.props === "object") {
    sources.push(instance.vnode.props as Record<string, unknown>);
  }
  if (instance?.props && typeof instance.props === "object") {
    sources.push(instance.props as Record<string, unknown>);
  }
  if (attrs && typeof attrs === "object") {
    sources.push(attrs as Record<string, unknown>);
  }
  hasClickListener.value = sources.some((src) =>
    Object.keys(src).some((k) => {
      const key = String(k);
      const lower = key.toLowerCase();
      return lower === "onclick" || lower.startsWith("onclick");
    }),
  );
};
onMounted(recomputeClickable);
onUpdated(recomputeClickable);
const clickable = () => hasClickListener.value;

const hasToolbarSlot = computed(
  () =>
    !!getCurrentInstance()?.slots["toolbarcenter"] ||
    !!getCurrentInstance()?.slots["toolbarend"] ||
    !!getCurrentInstance()?.slots["toolbarstart"] ||
    !!getCurrentInstance()?.slots["toolbar"],
);

// expose for template use
defineExpose({
  isIstLayoutCardEnabled,
  pTags,
  cardClicked,
  tagClicked,
  clickable,
  hasToolbarSlot,
});
</script>

<style lang="scss">
@import "../assets/variables";
.ist-card {
  &__tabs {
    display: none;
    overflow: hidden;
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      float: left;
      margin: 0 0.5em 0 0;
    }

    a {
      position: relative;
      background: #f0ad4e;
      padding: 0.7em 3.5em;
      float: left;
      text-decoration: none;
      color: #444;
      margin-left: 30px;
    }

    a:hover,
    a:hover::after,
    a:focus,
    a:focus::after {
      background: #fff;
    }

    a:focus {
      outline: 0;
    }

    a::after {
      content: "";
      position: absolute;
      z-index: 1;
      top: 0;
      right: -0.5em;
      bottom: 0;
      width: 1em;
      background: #f0ad4e;
      transform: skew(10deg);
      border-radius: 0 5px 0 0;
    }

    a::before {
      content: "";
      position: absolute;
      z-index: 1;
      top: 0;
      left: -0.5em;
      bottom: 0;
      width: 1em;
      background: #f0ad4e;
      -webkit-transform: skew(10deg);
      transform: skew(-12deg);
      border-radius: 5px 0px 0 0;
    }
  }

  &__splitter {
    border-top: 1px solid #ddd;
    margin: 0 16px;
  }
  &__move-to-bottom {
    margin-top: auto;
  }

  &__toolbar {
    border-bottom: 2px solid #ddd;

    & .q-field--filled {
      & .q-field__control {
        background-color: #fafafa;
        border: none;
        border-radius: 0;
      }
    }
  }

  &__body {
    background: #ffffff;
    border-radius: 16px;
    vertical-align: top;
    position: relative;
    border: 1px solid #ddd;
    display: flex;
    flex-direction: column;

    & .footer {
      border-top: 1px solid #ddd;
      display: inline-block;
      position: relative;
      width: 100%;
      height: 30px;
    }

    & > div:first-child,
    & > img:first-child {
      border-top: 0;
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
    }
    & > div:last-child,
    & > img:last-child {
      border-bottom: 0;
      border-bottom-left-radius: inherit;
      border-bottom-right-radius: inherit;
    }
    & > div:not(:first-child),
    & > img:not(:first-child) {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
    & > div:not(:last-child),
    & > img:not(:last-child) {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
    & > div {
      border-left: 0;
      border-right: 0;
      box-shadow: none;
    }

    @media screen and (min-width: 800px) {
      &__section {
        position: relative;
        padding: 16px;
        overflow: hidden;

        & > .q-table__card {
          border: 0 !important;
          border-radius: 0 !important;
          margin: 0 -16px -16px -16px;
        }
      }
    }

    @media screen and (max-width: 800px) {
      &__section {
        position: relative;
        padding: 0px;
        overflow: hidden;

        & > .q-table__card {
          border: 0 !important;
          border-radius: 0 !important;
        }
      }
    }
  }
}
.istcard {
  filter: drop-shadow(2px 2px 3px rgba(0, 0, 0, 0.12));
}

.ist-arrow-right_start {
  margin-left: -25px;
  margin-top: -8px;
}

.ist-arrow-right {
  padding: 6px 18px 06px 32px;
  margin-bottom: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.42857143;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  border: 1px solid transparent;
  color: #000;
  background-color: #f0ad4e;
  border-color: #f0ad4e;
  border-radius: 5px 2px 2px 5px !important;
  margin-top: 8px;
  float: left;
  position: relative;
  margin-right: 15px;
}

.ist-arrow-right::after {
  content: "";
  top: 4px;
  right: -11px;
  position: absolute;
  width: 24px;
  height: 24px;
  background: inherit;
  border-left-color: transparent;
  border-bottom-color: transparent;
  border-radius: 2px 5px 2px 20px;
  -webkit-transform: rotate(45deg);
  transform: rotate(45deg);
}
.iconstyle {
  text-align: center;
  width: 100%;
  padding-top: 8px;
  margin-left: 10px;
  font-size: xx-large !important;
}
.clickable .ist-card__body {
  cursor: pointer;
  -webkit-box-shadow: 2px 2px 5px -4px #000000;
  box-shadow: 2px 2px 5px -4px #000000;
}
.clickable {
  cursor: pointer;
}
.clickable:hover .ist-card__body {
  -webkit-box-shadow: 0px 0px 0px 0px #000000;
  box-shadow: 0px 0px 0px 0px #000000;
}
.ist-actionbar {
  flex-wrap: wrap !important;
}
</style>
